import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../api/client.js";
import { useAuth } from "../../auth/AuthContext.js";

/**
 * REVIEWS auto-production status bar (owner t940u + 09:14 IL decision, 2026-10-24): ONE compact bar, ALWAYS at the top of the Review page. With no run
 * active it shows the idle state ("No automatic run active", plus the last run's result and finish time); while a DR -> RE auto-production run is active
 * (daily run, Deep Research button or RE button) it switches to the end-to-end view. The active view covers the run end to end: start, the reel picks, per reel and
 * per persona (lead + copy) the current stage, per option the video step (generating, review, upscale and subtitles, final check, Drive upload), the
 * counts (videos done / target, in Drive / target), errors and skips, and the finish. Read-only apart from the existing Stop.
 *
 * Dashboard only: it reuses the existing owner-only routes (`GET /re/:t/auto/status`, `GET /re/:t/auto/runs/:id` = run + items + videos + drive uploads,
 * `GET /re/:t/auto/runs?limit=1`) and the existing video list (`GET /videos/:t?limit=100`) for each video's pipeline step. Auto-production is
 * owner-only: a non-owner gets 403 and sees no bar; an API without auto-production (503/404) shows "not available". Public wording rule (MANUAL style): no costs, providers or model names, so the engine's `report.line` (it carries the spend) and the
 * raw error texts are NOT shown here; errors are shown as short plain reasons.
 */

const REFRESH_MS = 15_000;
const OPTIONS_PER_REEL = 3;

type PersonaKey = "EMMA" | "YUNA_1";
interface AutoStatus { readonly enabled: boolean; readonly activeRun: { readonly id: string } | null; readonly personas: Readonly<Record<PersonaKey, string>>; readonly driveEnabled?: boolean }
interface RunRecord {
  readonly id: string; readonly trigger: string; readonly status: string; readonly error: string | null; readonly targetVideos: number;
  readonly plan?: { readonly candidates?: readonly { readonly code: string; readonly handle?: string }[]; readonly sourceVideoId?: string } | null;
  readonly createdAt: string; readonly finishedAt: string | null;
  readonly report?: { readonly finalized?: unknown } | null;
}
interface ItemRecord { readonly id: string; readonly slot: number; readonly role: "lead" | "copy"; readonly igCode: string | null; readonly reelNum: number | null; readonly personaId: string; readonly stage: string; readonly status: string; readonly analysisAttempts: number; readonly proposalsAttempts: number; readonly error: string | null }
interface VideoRecord { readonly id: string; readonly itemId: string; readonly ordinal: number; readonly attempt: number; readonly videoId: string | null; readonly status: string; readonly error: string | null }
interface DriveRecord { readonly videoId: string; readonly status: "pending" | "done" | "failed" }
export interface AutoRunView { readonly run: RunRecord; readonly items: readonly ItemRecord[]; readonly videos: readonly VideoRecord[]; readonly drive: readonly DriveRecord[] | null }
interface VideoRow { readonly id: string; readonly status: string }

const TRIGGER: Record<string, string> = { schedule: "Daily run", button_dr: "Deep Research button", button_re: "RE button" };
const STAGE: Record<string, string> = {
  handoff: "Getting the reel", project: "Setting up the project", analysis: "Analysis", approve_analysis: "Approving the analysis", proposals: "Writing the options",
  select: "Selecting the options", wait_lead: "Waiting for the lead", reuse: "Creating the copy", approve: "Approving the options", generate: "Starting the videos",
  videos: "Videos", done: "Done",
};
/** Pipeline steps of the whole run, in order (the step strip). */
export const RUN_STEPS = ["Start", "Reel picks", "Analysis", "Options", "Copy", "Videos", "Final", "Drive", "Finished"] as const;
type RunStep = (typeof RUN_STEPS)[number];
const STAGE_STEP: Record<string, RunStep> = { handoff: "Reel picks", project: "Reel picks", analysis: "Analysis", approve_analysis: "Analysis", proposals: "Options", select: "Options", wait_lead: "Copy", reuse: "Copy", approve: "Options", generate: "Videos", videos: "Videos", done: "Videos" };

/** A video's pipeline step in plain words (the run row + the video's own status + its Drive upload). */
export function videoStepLabel(row: VideoRecord, videoStatus: string | null, drive: DriveRecord | null, driveOn: boolean): string {
  if (row.status === "failed" || row.status === "rejected") return row.attempt === 1 ? "Failed, retrying" : "Failed";
  if (row.status === "finalized" || videoStatus === "finalized") {
    if (!driveOn) return "Done";
    if (!drive || drive.status === "pending") return "Uploading to Drive";
    return drive.status === "done" ? "In Drive" : "Drive upload failed";
  }
  switch (videoStatus) {
    case "draft": case "generating": return "Generating";
    case "ready_for_review": case "approved": return "Review";
    case "upscaling": case "subtitle_processing": return "Upscale and subtitles";
    case "ready_for_final_review": return "Final check";
    case "rejected": case "final_rejected": case "failed": return "Failed";
    default: return row.status === "review_approved" ? "Upscale and final check" : "Generating";
  }
}

/** A short, plain reason for an item / video error: never the raw text (it can carry amounts or provider names). */
export function plainReason(error: string | null): string | null {
  if (!error) return null;
  const e = error.toLowerCase();
  if (e.startsWith("reel dropped")) return "reel dropped";
  if (e.includes("voice_not_verified")) return "voice not verified";
  if (/\bcap\b/.test(e)) return "daily limit reached";
  if (e.includes("stopped by")) return "stopped";
  if (e.includes("too long")) return "source too long";
  if (e.includes("already produced")) return "already produced";
  if (e.includes("final asset missing")) return "final file missing";
  const code = /^([a-z]+(?:_[a-z]+)+)\b/.exec(error);
  return code ? code[1]!.replace(/_/g, " ") : "failed";
}

/**
 * The step strip of the status bar ("✓ Start → ✓ Reel picks → **Analysis** → …"): done steps ticked and muted, the current one bold. Shared with the Telegram daily
 * publish bar (owner t1149u: "the same status bar as the videos have").
 */
export function StepStrip({ steps, current, testId }: { readonly steps: readonly string[]; readonly current: number; readonly testId: string }) {
  return (
    <ol className="auto-run-steps" data-testid={testId} style={{ display: "flex", gap: 6, listStyle: "none", padding: 0, margin: "6px 0", flexWrap: "wrap" }}>
      {steps.map((s, i) => (
        <li key={s} data-step={s} data-current={i === current ? "true" : undefined} className={i < current ? "muted" : undefined} style={i === current ? { fontWeight: 700, color: "var(--accent)" } : undefined}>
          {i < current ? "✓ " : ""}{s}{i < steps.length - 1 ? " →" : ""}
        </li>
      ))}
    </ol>
  );
}

const time = (iso: string): string => new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Jerusalem", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
const finishedText = (status: string): string => (status === "done" ? "Finished" : status === "partial" ? "Finished with gaps" : status === "stopped" ? "Stopped" : "Failed");

/** The idle line: no run active (owner 09:14 IL), with the last run's result and finish time when there is one. */
export function idleText(enabled: boolean, last: RunRecord | null): { readonly line: string; readonly last: string | null } {
  const line = enabled ? "No automatic run active" : "No automatic run active · automatic runs are switched off";
  if (!last || last.status === "running") return { line, last: null };
  const fin = typeof last.report?.finalized === "number" ? ` · ${last.report.finalized}/${last.targetVideos} videos done` : "";
  const when = last.finishedAt ?? last.createdAt;
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jerusalem" }).format(new Date(when));
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jerusalem" }).format(new Date());
  return { line, last: `Last run: ${TRIGGER[last.trigger] ?? "Automatic run"} · ${finishedText(last.status).toLowerCase()} ${day === today ? "" : `${day} `}${time(when)}${last.targetVideos > 0 ? fin : " · nothing to produce"}` };
}

export interface BarModel {
  readonly running: boolean;
  readonly headline: string;
  readonly step: RunStep;
  readonly done: number;
  readonly inDrive: number | null;
  readonly target: number;
  readonly problems: number;
  readonly rows: readonly { readonly key: string; readonly reel: string; readonly persona: string; readonly stage: string; readonly options: readonly string[]; readonly reason: string | null; readonly state: string }[];
}

/** Pure: the bar's content from the run view and the video statuses (tested directly). */
export function buildBarModel(v: AutoRunView, personas: Readonly<Record<PersonaKey, string>>, videoStatus: ReadonlyMap<string, string>, driveOn: boolean): BarModel {
  const { run, items, videos } = v;
  const running = run.status === "running";
  const drive = new Map((v.drive ?? []).map((d) => [d.videoId, d]));
  const key = (personaId: string): string => (personaId === personas.YUNA_1 ? "YUNA_1" : personaId === personas.EMMA ? "EMMA" : "persona");
  const handle = new Map((run.plan?.candidates ?? []).map((c) => [c.code, c.handle]));
  const latest = (itemId: string, ordinal: number): VideoRecord | null => videos.filter((x) => x.itemId === itemId && x.ordinal === ordinal).sort((a, b) => b.attempt - a.attempt)[0] ?? null;
  const isDone = (x: VideoRecord): boolean => x.status === "finalized" || (x.videoId !== null && videoStatus.get(x.videoId) === "finalized");
  const doneSet = new Set(videos.filter(isDone).map((x) => `${x.itemId}:${x.ordinal}`));
  const inDrive = driveOn ? new Set(videos.filter((x) => isDone(x) && x.videoId && drive.get(x.videoId)?.status === "done").map((x) => `${x.itemId}:${x.ordinal}`)).size : null;
  const sorted = [...items].sort((a, b) => a.slot - b.slot || (a.role === b.role ? 0 : a.role === "lead" ? -1 : 1));
  const rows = sorted.map((it) => {
    const reel = `Reel ${it.slot}${it.igCode ? ` (${handle.get(it.igCode) ? `@${handle.get(it.igCode)}` : it.igCode})` : ""}`;
    const persona = `${key(it.personaId)} ${it.role === "lead" ? "lead" : "copy"}`;
    const opts: string[] = [];
    for (let o = 1; o <= OPTIONS_PER_REEL; o++) {
      const r = latest(it.id, o);
      if (r) opts.push(`${o}: ${videoStepLabel(r, r.videoId ? videoStatus.get(r.videoId) ?? null : null, r.videoId ? drive.get(r.videoId) ?? null : null, driveOn)}`);
    }
    const attempts = it.stage === "analysis" && it.analysisAttempts > 1 ? ` (try ${it.analysisAttempts})` : it.stage === "proposals" && it.proposalsAttempts > 1 ? ` (try ${it.proposalsAttempts})` : "";
    const stage = it.status === "skipped" ? "Skipped" : it.status === "failed" ? "Failed" : it.status === "done" ? "Done" : it.status === "pending" && it.stage !== "wait_lead" ? "Queued" : `${STAGE[it.stage] ?? it.stage}${attempts}`;
    return { key: it.id, reel, persona, stage, options: opts, reason: it.status === "skipped" || it.status === "failed" || it.error ? plainReason(it.error) : null, state: it.status };
  });
  const problems = items.filter((i) => i.status === "failed" || i.status === "skipped").length + videos.filter((x) => (x.status === "failed" || x.status === "rejected") && x.attempt === 2).length;
  let step: RunStep;
  if (!running) step = "Finished";
  else if (!items.length) step = "Start";
  else {
    const open = sorted.filter((i) => i.status === "pending" || i.status === "active");
    const steps = open.filter((i) => i.stage !== "wait_lead").map((i) => STAGE_STEP[i.stage] ?? "Videos");
    const order = (s: RunStep): number => RUN_STEPS.indexOf(s);
    const allVideos = open.every((i) => i.stage === "videos" || i.stage === "done");
    const inFlight = videos.filter((x) => !isDone(x) && x.status !== "failed" && x.status !== "rejected");
    const finalPhase = allVideos && inFlight.length > 0 && inFlight.every((x) => x.status === "review_approved" || ["upscaling", "subtitle_processing", "ready_for_final_review"].includes(x.videoId ? videoStatus.get(x.videoId) ?? "" : ""));
    step = !open.length ? "Drive" : allVideos && inFlight.length === 0 ? (driveOn ? "Drive" : "Final") : finalPhase ? "Final" : [...steps].sort((a, b) => order(a) - order(b))[0] ?? "Copy";
  }
  const trig = TRIGGER[run.trigger] ?? "Automatic run";
  const headline = running ? `Auto-production running · ${trig} · started ${time(run.createdAt)}` : `Auto-production ${finishedText(run.status).toLowerCase()} · ${trig} · ${run.finishedAt ? `ended ${time(run.finishedAt)}` : ""}`.replace(/ · $/, "");
  return { running, headline, step, done: doneSet.size, inDrive, target: run.targetVideos, problems, rows };
}

export function ReviewsAutoRunBar({ tenantId }: { readonly tenantId: string }) {
  const { csrfToken } = useAuth();
  const [view, setView] = useState<AutoRunView | null>(null);
  const [model, setModel] = useState<BarModel | null>(null);
  const [idle, setIdle] = useState<{ readonly line: string; readonly last: string | null } | null>(null);
  const [unavailable, setUnavailable] = useState<"none" | "hidden" | "unavailable">("none");
  const [msg, setMsg] = useState<string | null>(null);
  const off = useRef(false);
  useEffect(() => {
    let live = true;
    off.current = false;
    const load = async (): Promise<void> => {
      if (off.current) return;
      try {
        const s = await api.get<AutoStatus>(`/re/${tenantId}/auto/status`);
        if (!live || !s) return;
        setUnavailable("none");
        const runId = s.activeRun?.id ?? null;
        if (!runId) {
          let last: RunRecord | null = null;
          try { last = (await api.get<{ runs: readonly RunRecord[] }>(`/re/${tenantId}/auto/runs?limit=1`))?.runs?.[0] ?? null; } catch { /* idle line without the last run */ }
          if (!live) return;
          setView(null); setModel(null); setIdle(idleText(s.enabled, last)); return;
        }
        const v = await api.get<AutoRunView>(`/re/${tenantId}/auto/runs/${encodeURIComponent(runId)}`);
        if (!live || !v?.run) return;
        const statuses = new Map<string, string>();
        if (v.videos.some((x) => x.videoId)) {
          try {
            const list = await api.get<{ videos: readonly VideoRow[] }>(`/videos/${tenantId}?limit=100`);
            for (const r of list?.videos ?? []) statuses.set(r.id, r.status);
          } catch { /* the bar still shows the run's own video states */ }
        }
        if (!live) return;
        setView(v);
        setModel(buildBarModel(v, s.personas, statuses, v.drive !== null && s.driveEnabled !== false));
      } catch (e) {
        if (live && e instanceof ApiError && (e.status === 403 || e.status === 503 || e.status === 404)) { off.current = true; setView(null); setModel(null); setUnavailable(e.status === 403 ? "hidden" : "unavailable"); }
        else if (live) setUnavailable((u) => (u === "hidden" ? u : "unavailable")); // a network blip: keep polling; an active view already shown stays
      }
    };
    void load();
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => { live = false; clearInterval(t); };
  }, [tenantId]);

  if (unavailable === "hidden") return null;
  if (!view || !model) {
    const line = unavailable === "unavailable" ? "Automatic run status is not available" : idle?.line ?? "Checking for an automatic run…";
    return (
      <div className="card auto-run-bar" data-testid="reviews-auto-bar" data-state="idle" role="status" aria-live="polite">
        <strong data-testid="reviews-auto-bar-headline">{line}</strong>
        {idle?.last && unavailable === "none" ? <span className="muted" data-testid="reviews-auto-bar-last" style={{ marginLeft: 12 }}>{idle.last}</span> : null}
      </div>
    );
  }
  const runId = view.run.id;
  const stop = async (): Promise<void> => {
    if (!window.confirm("Stop this run? No new step starts; videos already submitted stay in the normal pipeline.")) return;
    try { await api.post(`/re/${tenantId}/auto/runs/${encodeURIComponent(runId)}/stop`, csrfToken, {}); setMsg("Stop requested."); }
    catch (e) { const b = e instanceof ApiError ? (e.body as { message?: string } | null) : null; setMsg(b?.message ?? "Stop failed."); }
  };
  const current = RUN_STEPS.indexOf(model.step);
  return (
    <div className="card auto-run-bar" data-testid="reviews-auto-bar" data-state={model.running ? "running" : view.run.status} role="status" aria-live="polite">
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <strong data-testid="reviews-auto-bar-headline">{model.headline}</strong>
        <span data-testid="reviews-auto-bar-counts">
          Videos done {model.done}/{model.target}{model.inDrive !== null ? ` · In Drive ${model.inDrive}/${model.target}` : ""}{model.problems ? ` · ${model.problems} problem${model.problems === 1 ? "" : "s"}` : ""}
        </span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {model.running ? <button type="button" className="danger" data-testid="reviews-auto-bar-stop" onClick={() => void stop()}>Stop</button> : null}
        </span>
      </div>
      <StepStrip steps={RUN_STEPS} current={current} testId="reviews-auto-bar-steps" />
      {model.rows.length ? (
        <ul data-testid="reviews-auto-bar-rows" style={{ margin: 0, paddingLeft: 18 }}>
          {model.rows.map((r) => (
            <li key={r.key} data-testid="reviews-auto-bar-row" data-state={r.state}>
              {r.reel} · {r.persona}: {r.stage}{r.options.length ? ` — ${r.options.join(", ")}` : ""}{r.reason ? ` (${r.reason})` : ""}
            </li>
          ))}
        </ul>
      ) : <p className="muted" style={{ margin: 0 }}>Starting: picking the reels…</p>}
      {msg ? <p className="muted" style={{ margin: 0 }}>{msg}</p> : null}
    </div>
  );
}
